import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { PageHero } from '../components/layout/PageHero';
import { DataTable } from '../components/procurement/DataTable';
import { Button } from '../components/ui/Button';
import { StatusPill } from '../components/ui/StatusPill';
import { DownloadIcon } from 'lucide-react';
import { procurementPlan } from '../data/procurement';
export function AnnualPlan() {
  const [selectedYear, setSelectedYear] = useState('2026');
  const columns = [{
    key: 'item',
    label: 'Item',
    sortable: true
  }, {
    key: 'category',
    label: 'Category',
    sortable: true
  }, {
    key: 'estimatedValue',
    label: 'Estimated Value',
    sortable: true
  }, {
    key: 'procurementMethod',
    label: 'Procurement Method',
    sortable: false
  }, {
    key: 'quarter',
    label: 'Quarter',
    sortable: true
  }, {
    key: 'status',
    label: 'Status',
    sortable: false
  }];
  const tableData = procurementPlan.map(item => ({
    ...item,
    status: <StatusPill status={item.status === 'in-progress' ? 'active' : item.status === 'completed' ? 'completed' : 'open'} data-id="element-642" data-mp="3en7ige5i9jp3">
        {item.status.toUpperCase().replace('-', ' ')}
      </StatusPill>
  }));
  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'landscape'
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 15;
    // Letterhead band
    doc.setFillColor(0, 104, 55);
    doc.rect(0, 0, pageWidth, 26, 'F');
    // Gold accent stripe
    doc.setFillColor(252, 209, 22);
    doc.rect(0, 26, pageWidth, 2, 'F');
    // AU emblem
    doc.setFillColor(255, 255, 255);
    doc.circle(marginX + 7, 13, 7, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('AU', marginX + 7, 15, {
      align: 'center'
    });
    // Letterhead text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('AFRICAN UNION COMMISSION', marginX + 20, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Procurement Department', marginX + 20, 18);
    doc.text('London Office, United Kingdom', marginX + 20, 23);
    // Document title
    doc.setTextColor(26, 31, 46);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`ANNUAL PROCUREMENT PLAN — ${selectedYear}`, pageWidth / 2, 42, {
      align: 'center'
    });
    doc.setDrawColor(252, 209, 22);
    doc.setLineWidth(0.8);
    doc.line(pageWidth / 2 - 35, 46, pageWidth / 2 + 35, 46);
    // Subtitle
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Overview of planned procurement activities and timelines for the fiscal year', pageWidth / 2, 52, {
      align: 'center'
    });
    // Table
    const tableStartY = 62;
    const colWidths = [62, 30, 32, 60, 24, 28];
    const headers = ['Item', 'Category', 'Estimated Value', 'Procurement Method', 'Quarter', 'Status'];
    const rowHeight = 12;
    // Header row
    let x = marginX;
    doc.setFillColor(0, 104, 55);
    doc.rect(marginX, tableStartY, colWidths.reduce((a, b) => a + b, 0), 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    headers.forEach((h, i) => {
      doc.text(h, x + 2, tableStartY + 6);
      x += colWidths[i];
    });
    // Body rows
    let y = tableStartY + 9;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    procurementPlan.forEach((item, rowIndex) => {
      // Zebra stripe
      if (rowIndex % 2 === 0) {
        doc.setFillColor(250, 247, 242);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(marginX, y, colWidths.reduce((a, b) => a + b, 0), rowHeight, 'F');
      doc.setTextColor(26, 31, 46);
      x = marginX;
      const values = [item.item, item.category, item.estimatedValue, item.procurementMethod, item.quarter];
      values.forEach((val, i) => {
        const wrapped = doc.splitTextToSize(String(val), colWidths[i] - 4);
        doc.text(wrapped.slice(0, 2), x + 2, y + 5);
        x += colWidths[i];
      });
      // Status pill
      const statusLabel = item.status.toUpperCase().replace('-', ' ');
      const pillX = x + 2;
      const pillY = y + 3;
      const pillW = colWidths[5] - 6;
      const pillH = 6;
      if (item.status === 'in-progress') {
        doc.setFillColor(232, 243, 237);
        doc.setTextColor(0, 104, 55);
      } else if (item.status === 'completed') {
        doc.setFillColor(229, 231, 235);
        doc.setTextColor(80, 80, 80);
      } else {
        doc.setFillColor(254, 243, 199);
        doc.setTextColor(146, 64, 14);
      }
      doc.roundedRect(pillX, pillY, pillW, pillH, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(statusLabel, pillX + pillW / 2, pillY + 4, {
        align: 'center'
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      y += rowHeight;
    });
    // Table border
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.rect(marginX, tableStartY, colWidths.reduce((a, b) => a + b, 0), y - tableStartY);
    // Summary block
    const totalValue = procurementPlan.reduce((sum, item) => {
      const v = parseFloat(item.estimatedValue.replace(/[£,]/g, ''));
      return sum + (isNaN(v) ? 0 : v);
    }, 0);
    const summaryY = y + 8;
    doc.setFillColor(232, 243, 237);
    doc.rect(marginX, summaryY, colWidths.reduce((a, b) => a + b, 0), 14, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Total Planned Items: ${procurementPlan.length}`, marginX + 4, summaryY + 9);
    doc.text(`Total Estimated Value: £${totalValue.toLocaleString('en-GB')}`, marginX + colWidths.reduce((a, b) => a + b, 0) - 4, summaryY + 9, {
      align: 'right'
    });
    // Footer
    const footerY = pageHeight - 18;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.line(marginX, footerY, pageWidth - marginX, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Procurement Department  |  procurement@aucommission.org  |  +44 (0) 20 4571 0123', pageWidth / 2, footerY + 5, {
      align: 'center'
    });
    doc.text('African Union Commission, London Office  |  www.africanunion.org', pageWidth / 2, footerY + 10, {
      align: 'center'
    });
    doc.setTextColor(150, 150, 150);
    doc.text(`Officially issued document  |  Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, footerY + 14, {
      align: 'center'
    });
    doc.save(`AU-Annual-Procurement-Plan-${selectedYear}.pdf`);
  };
  return <div data-id="element-643" data-mp="1v8nd6lkx97yp">
      <PageHero title="Annual Procurement Plan" subtitle="Planned procurement activities and timelines for the fiscal year" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Annual Plan'
    }]} data-id="element-644" data-mp="1v8x4qdlm7n6i" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-645" data-mp="9tzqj14vrnn1">
        <div className="bg-white rounded-lg border border-gray-border p-6 mb-8" data-id="element-646" data-mp="9wc7pc2tdtz1">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-647" data-mp="eux410ey3ti7">
            <div data-id="element-648" data-mp="16i8dewolgx7u">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-649" data-mp="16ekibt3i42r0">
                Procurement Plan {selectedYear}
              </h2>
              <p className="text-gray-600" data-id="element-650" data-mp="whxxh4b6dboz">
                Overview of planned procurement activities for the fiscal year
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-651" data-mp="rm0stv99xts1">
              <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-652" data-mp="1p6daxmenrly7">
                <option value="2026" data-id="element-653" data-mp="1ais5vmy83cnn">2026</option>
                <option value="2025" data-id="element-654" data-mp="1ahz83bh8rxhn">2025</option>
                <option value="2024" data-id="element-655" data-mp="27xmnk4jq6qu0">2024</option>
              </select>
              <Button variant="secondary" onClick={handleDownloadPDF} className="flex items-center gap-2" data-id="element-656" data-mp="27zqyv7jyes4b">
                <DownloadIcon size={20} data-id="element-657" data-mp="2bhen6s5rv683" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-border overflow-hidden" data-id="element-658" data-mp="2kgi71ycrjpnn">
          <DataTable columns={columns} data={tableData} data-id="element-659" data-mp="2kiuzj5g5diey" />
        </div>

        <div className="mt-8 bg-au-green-light rounded-lg p-6" data-id="element-660" data-mp="2ph687cuphmvl">
          <h3 className="font-bold text-charcoal mb-2" data-id="element-661" data-mp="2ufhjls0ng5fx">
            About the Annual Procurement Plan
          </h3>
          <p className="text-gray-700" data-id="element-662" data-mp="2yeypgmjzn4lm">
            The Annual Procurement Plan provides transparency on upcoming
            procurement activities, estimated values, and timelines. This allows
            potential suppliers to prepare and plan their participation in AU
            procurement processes. All procurement activities are conducted in
            accordance with AU procurement policies and procedures.
          </p>
        </div>
      </div>
    </div>;
}